<template>
  <v-container fluid>
    <back-link-component :to="{ name: 'FieldListAsList' }" :label="$tt('customFieldsList')" />
    <h2>{{ $tt('customFieldCreateWithMapTitle') }}</h2>
    <v-row class="align-center">
      <v-col cols="12" sm="6">
        <v-form ref="form" lazy-validation class="ml-1">
          <v-text-field
            v-model="fieldName"
            :label="$tt('customFieldCreateWithMapFieldName')"
            :error-messages="fieldNameError"
            required
            @input="$v.fieldName.$touch()"
            @blur="$v.fieldName.$touch()"
          />
        </v-form>
      </v-col>
      <v-col cols="12" sm="6">
        <v-btn color="secondary" :to="{ name: 'FieldListAsList' }" class="mr-2">
          {{ $tt('customCancel') }}
        </v-btn>
        <v-btn @click="createField()" color="primary" :disabled="isCreateBtnDisabled">
          {{ $tt('customFieldCreateWithMapCreate') }}
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <gmap-map
          ref="googleMapRef"
          class="mt-4"
          :center="mapCenter"
          :zoom="mapZoom"
          :options="{ streetViewControl: false, rotateControl: false, fullscreenControl: false }"
          map-type-id="roadmap"
          style="height: 70vh;position:relative;"
        >
          <GmapMarker
            :key="index"
            v-for="(marker, index) in markers"
            :position="marker.position"
            :clickable="true"
            :label="marker.label"
          />
          <div class="current-location-btn on-map-btn" @click="findCurrentLocation()">
            <v-icon>mdi-crosshairs-gps</v-icon>
          </div>
          <div class="finish-btn on-map-btn" v-if="isDrawing && vertexCount >= 3" @click="finishDrawing()">
            <v-icon color="primary">mdi-check-bold</v-icon>
          </div>
          <div class="undo-btn on-map-btn" v-if="isDrawing && vertexCount >= 1" @click="undoVertex()">
            <v-icon>mdi-undo-variant</v-icon>
          </div>
          <div class="delete-btn on-map-btn" v-if="isDeleteBtn" @click="deletePolygon()">
            <v-icon>mdi-trash-can</v-icon>
          </div>
          <div class="draw-hint" v-if="isDrawing">{{ $tt('customFieldDrawHint') }}</div>
        </gmap-map>
      </v-col>
    </v-row>
    <now-loading-overlay-component :nowLoading="nowLoading" />
  </v-container>
</template>

<script>
import NowLoadingOverlayComponent from '@/components/NowLoadingOverlayComponent'
import MapMixin from '@/components/mixins/map-mixin'
import GoogleMapMixin from '@/components/mixins/google-map-mixin'
import * as Constants from '@/lib/constants'
import storage from '@/lib/storage'
import Toast from '@/lib/toast'

import { validationMixin } from 'vuelidate'
import { required } from 'vuelidate/lib/validators'

export default {
  name: 'FieldCreate',
  mixins: [MapMixin, validationMixin, GoogleMapMixin],
  components: { NowLoadingOverlayComponent },
  data() {
    return {
      fieldName: undefined,
      coordinates: [],
      nowLoading: false,
      drawPolygon: null,
      isDeleteBtn: false,
      isCreateBtnDisabled: true,
      isDrawing: false,
      vertexCount: 0,
      mapClickListener: null,
    }
  },
  mounted() {
    this.$store.dispatch('setFieldGeoJsons', { type: 'FeatureCollection', features: [] })
    this.drawAllFieldsOnGoogleMap()

    this.$refs.googleMapRef.$mapPromise.then(map => {
      map.setTilt(0)
      map.data.addGeoJson(this.$store.getters.fieldGeoJsons)
      map.data.setStyle(feature => {
        return {
          fillColor: Constants.FIELD_FILL_COLOR,
          fillOpacity: Constants.FIELD_FILL_OPACITY,
          strokeColor: Constants.FIELD_FILL_COLOR,
          strokeWeight: 1,
        }
      })

      if (this.$store.getters.fieldGeoJsons.features.length > 0) {
        let bounds = new google.maps.LatLngBounds()

        map.data.forEach(feature => {
          feature.getGeometry().forEachLatLng(latlng => {
            bounds.extend(latlng)
          })
        })
        map.fitBounds(bounds)
      }
    })
    this.startDrawing()

    // DEL / BackSpace で直前の頂点を削除 (テキスト入力中は除く)
    this.keydownHandler = e => this.onKeydown(e)
    document.addEventListener('keydown', this.keydownHandler)
  },

  beforeDestroy() {
    document.removeEventListener('keydown', this.keydownHandler)
    if (this.mapClickListener) {
      this.mapClickListener.remove()
      this.mapClickListener = null
    }
  },

  computed: {
    mapCenter() {
      return this.getDefaultMapCenter()
    },

    mapZoom() {
      return this.getDefaultZoomLevel()
    },

    fieldNameError() {
      const errors = []
      if (!this.$v.fieldName.$dirty) return errors
      !this.$v.fieldName.required && errors.push(this.$tt('customFieldCreateWithMapNoFieldNameMessage'))
      return errors
    },
  },

  validations: {
    fieldName: { required },
  },

  methods: {
    // DrawingManager (v3.65 で廃止) 非依存のクリック描画:
    // 地図クリックで頂点追加 → editable Polygon が即時プレビュー → チェックボタンで確定
    startDrawing() {
      this.isDrawing = true
      this.vertexCount = 0
      this.$refs.googleMapRef.$mapPromise.then(map => {
        // 描画中であることが分かるように十字カーソルにする
        map.setOptions({ draggableCursor: 'crosshair' })
        this.mapClickListener = map.addListener('click', e => this.addVertex(map, e.latLng))
      })
    },
    addVertex(map, latLng) {
      if (!this.drawPolygon) {
        this.drawPolygon = new google.maps.Polygon({
          map,
          paths: [latLng],
          fillColor: Constants.FIELD_FILL_COLOR,
          fillOpacity: Constants.FIELD_FILL_OPACITY,
          strokeColor: Constants.FIELD_CURRENT_LINE_COLOR,
          strokeWeight: 2,
          clickable: false, // 描画中は地図クリックを透過させる
          editable: true,
          zIndex: 1,
        })
      } else {
        this.drawPolygon.getPath().push(latLng)
      }
      this.vertexCount = this.drawPolygon.getPath().getLength()
    },
    // 直前にクリックした頂点を1つ削除する (戻るボタン / DEL / BackSpace)
    undoVertex() {
      if (!this.isDrawing || !this.drawPolygon) {
        return
      }
      const path = this.drawPolygon.getPath()
      if (path.getLength() > 0) {
        path.pop()
      }
      this.vertexCount = path.getLength()
      if (this.vertexCount === 0) {
        this.drawPolygon.setMap(null)
        this.drawPolygon = null
      }
    },
    onKeydown(e) {
      if (e.key !== 'Backspace' && e.key !== 'Delete') {
        return
      }
      const t = e.target
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) {
        return
      }
      if (!this.isDrawing || this.vertexCount === 0) {
        return
      }
      e.preventDefault()
      this.undoVertex()
    },
    finishDrawing() {
      if (!this.drawPolygon || this.drawPolygon.getPath().getLength() < 3) {
        return
      }
      this.isDrawing = false
      if (this.mapClickListener) {
        this.mapClickListener.remove()
        this.mapClickListener = null
      }
      this.$refs.googleMapRef.$mapPromise.then(map => {
        map.setOptions({ draggableCursor: null })
      })
      this.drawPolygon.setOptions({ clickable: true, draggable: true })
      this.setCoordinates()
      this.isDeleteBtn = true
      this.isCreateBtnDisabled = false
      const vm = this
      google.maps.event.addListener(this.drawPolygon, 'dragend', function() {
        vm.setCoordinates()
      })
      google.maps.event.addListener(this.drawPolygon, 'mouseup', function() {
        vm.setCoordinates()
      })
    },
    deletePolygon() {
      if (this.drawPolygon) {
        this.drawPolygon.setMap(null)
        this.drawPolygon = null
      }
      this.isDeleteBtn = false
      this.isCreateBtnDisabled = true
      this.startDrawing()
    },
    setCoordinates() {
      const coordinates = this.drawPolygon.getPath().getArray()
      let coordinate = []
      let point0 = null
      this.coordinates = []
      coordinates.forEach(point => {
        let points = []
        points.push(point.toJSON().lng)
        points.push(point.toJSON().lat)
        if (point0 === null) {
          point0 = points
        }
        coordinate.push(points)
      })
      coordinate.push(point0)
      this.coordinates.push(coordinate)
    },
    findCurrentLocation() {
      this.nowLoading = true
      if (window.navigator.geolocation) {
        window.navigator.geolocation.getCurrentPosition(
          response => {
            const lat = response.coords.latitude
            const lng = response.coords.longitude
            var latLng = new google.maps.LatLng(lat, lng)
            this.$refs.googleMapRef.$mapPromise.then(map => {
              let bounds = new google.maps.LatLngBounds(latLng)
              map.fitBounds(bounds)
            })
            this.nowLoading = false
          },
          error => {
            console.error(error)
            this.nowLoading = false
          }
        )
      }
    },
    addCoordinate(coordinate) {
      this.coordinates.push(coordinate)
    },

    createField() {
      this.$v.$touch()
      if (this.$v.$error) {
        console.error('Validation Error')
        return
      }

      let features = [
        {
          type: 'Feature',
          geometry: {
            type: 'Polygon',
            coordinates: this.coordinates,
          },
          properties: {
            Name: this.fieldName,
          },
        },
      ]

      const featureSize = features.length

      if (featureSize > 1) {
        // Too many polygons
        Toast.i18nError('customFieldCreateWithMapTooManyFieldsMessage')
        return
      } else if (featureSize === 0) {
        // No polygon
        Toast.i18nError('customFieldCreateWithMapNoFieldMessage')
        return
      }

      for (let i = 0; i < features.length; i++) {
        features[i].properties.Name = this.fieldName // set Field Name - all items
      }

      const request = { features: features, propertyAsName: 'Name' } // set Parameters for API

      this.$store.dispatch('createField', { request: request, fieldName: this.fieldName })
    },

    isLanguage(lang) {
      return storage.getUserLangConsideringDefault() === lang
    },
  },
}
</script>
